import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, output } from '@angular/core';

import type { User } from '@core/account-types';
import { AdminsAvatarsComponent } from '@core/account-ui';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import type { Visibility } from '@core/visibility-types';
import { VisibilityLevel } from '@core/visibility-types';

import type { VisibilitySelectLabels } from './types';
import type { VisibilityInfoContext } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import { VisibilitySelectorDropdownComponent } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import type { RoleWithAdmins } from '../../types';

@Component({
  selector: 'rt-visibility-selector',
  templateUrl: 'visibility-selector.component.html',
  styleUrls: ['./visibility-selector.component.less'],
  host: {
    '[class.wrap-on-smaller-resolutions]': 'wrapOnSmallerResolutions',
    '[class.editable]': 'editable',
  },
  imports: [
    IconComponent,
    RtTooltip,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    NgTemplateOutlet,
    ConnectedOverlayDirective,
    VisibilitySelectorDropdownComponent,
    AdminsAvatarsComponent,
    IconButtonComponent,
    TranslatePipe,
  ],
})
export class VisibilitySelectorComponent implements OnChanges {
  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  public i18n = inject(I18nService);

  @Input() public visibility: Visibility;
  @Input() public admins: User[];
  @Input() public disallowedUsers: User[];
  @Input() public rolesWithAdmins: RoleWithAdmins[];
  @Input() public hasOnlyMeLevel: boolean = true;
  @Input() public hasSelectTeamMembers: boolean = true;
  @Input() public hasLabel: boolean = true;
  @Input() public hasLockIcon: boolean = true;
  @Input() public editable: boolean = true;
  @Input() public allowAdminsChange: boolean = true;
  @Input() public wrapOnSmallerResolutions: boolean = false;
  @Input() public avatarMaxLength: number = 3;
  @Input() public infoContext: VisibilityInfoContext = 'view';
  @Input() public buttonVariant: 'plain' | 'outline' = 'plain';
  @Input() public protectedUsersPosition: 'outside' | 'inside' = 'outside';
  @Input() public labels: VisibilitySelectLabels;
  @Input() public avatarSize: 'small' | 'small-x' = 'small';

  @Input() public dropdownPositions: ConnectionPositionPair[];
  @Input() public dropdownOffsetX: number = 0;
  @Input() public dropdownOffsetY: number = 0;

  public visibilityDropdownToggle = output<boolean>();
  public visibilityChanges = output<VisibilityLevel>();

  public dropdownOpened: boolean = false;

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.infoContext && this.infoContext && !this.labels) {
      this.labels = this.getDefaultLabels(this.infoContext);
    }
  }

  public ngOnInit() {
    if (!this.labels) {
      this.labels = this.getDefaultLabels(this.infoContext);
    }
  }

  public onChangeVisibilityLevel(level: VisibilityLevel): void {
    this.visibilityChanges.emit(level);
    this.cdRef.detectChanges();
  }

  public buttonClick(): void {
    if (!this.editable) {
      return;
    }
    this.toggleDropdown();
  }

  public toggleDropdown(): void {
    this.dropdownOpened = !this.dropdownOpened;
    this.visibilityDropdownToggle.emit(this.dropdownOpened);
    this.cdRef.detectChanges();
  }

  public isPrivate(): boolean {
    return [VisibilityLevel.Protected, VisibilityLevel.Private].includes(this.visibility.level);
  }

  public setProtectedLevel(): void {
    if (!this.editable || !this.allowAdminsChange) {
      return;
    }
    this.visibilityChanges.emit(VisibilityLevel.Protected);
  }

  private getDefaultLabels(context: VisibilityInfoContext): VisibilitySelectLabels {
    switch (context) {
      case 'access':
        return {
          selected: {
            protected: this.i18n.translate('app.visibility.access_by'),
            public: this.i18n.translate('app.visibility.access_by_everyone'),
            private: this.i18n.translate('app.visibility.access_by_me'),
          },
        };

      default:
      case 'view':
        return {
          selected: {
            protected: this.i18n.translate('app.visibility.selector.protected.value'),
            public: this.i18n.translate('app.visibility.selector.public.value'),
            private: this.i18n.translate('app.visibility.selector.private.value'),
          },
        };
    }
  }
}
